import { db } from '../firebaseClient';
import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as fsLimit,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

const BLOG_COLLECTION = 'blogPosts';

/**
 * Turn a title into a URL-safe slug.
 * "Why AI Fluency Matters!" -> "why-ai-fluency-matters"
 */
/**
 * Estimates reading time from HTML body content, Substack/Medium style.
 * Strips tags, counts words, assumes ~200 words/minute.
 */
export const estimateReadingTime = (html = '') => {
  const text = html.replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
};

export const slugify = (text = '') =>
  text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Checks whether a slug is already in use by another post. Pass
 * excludeId when editing a post to ignore that post's own slug.
 *
 * @param {string} slug
 * @param {string} [excludeId]
 * @returns {Promise<boolean>}
 */
export const isSlugTaken = async (slug, excludeId = null) => {
  try {
    const q = query(collection(db, BLOG_COLLECTION), where('slug', '==', slug));
    const snap = await getDocs(q);
    if (snap.empty) return false;
    if (excludeId) return snap.docs.some((d) => d.id !== excludeId);
    return true;
  } catch (err) {
    console.error('[blogHelpers] Error checking slug:', err);
    return false;
  }
};

/**
 * Checks whether the currently signed-in user has a platformAdmins doc.
 * Used to gate access to the admin composer UI. Relies on the Firestore
 * rule `allow read: if isPlatformAdmin()` on platformAdmins/{uid} — a
 * non-admin's read will be denied and this resolves to false rather than
 * throwing up the console.
 *
 * @returns {Promise<boolean>}
 */
export const isCurrentUserPlatformAdmin = async () => {
  const user = getAuth().currentUser;
  if (!user) return false;
  try {
    const snap = await getDoc(doc(db, 'platformAdmins', user.uid));
    return snap.exists();
  } catch (err) {
    // Permission denied (not an admin) or any other error -> treat as not admin.
    return false;
  }
};

/**
 * Create a new blog post document. Only succeeds if the signed-in user
 * is a platformAdmin, per the blogPosts security rule.
 *
 * @param {object} postData - fields matching the blogPosts schema
 * @returns {Promise<{ id: (string|null), error: (Error|null) }>}
 */
/**
 * Generates a random token for shareable preview links. Not meant as a
 * real auth credential (see caveat in the preview-page comment below) —
 * just long enough that it isn't guessable by scanning post ids.
 *
 * @returns {string}
 */
export const generatePreviewToken = () => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const createBlogPost = async (postData) => {
  try {
    const docRef = await addDoc(collection(db, BLOG_COLLECTION), {
      ...postData,
      previewToken: postData.previewToken || generatePreviewToken(),
      publishedAt: postData.publishedAt || serverTimestamp(),
    });
    return { id: docRef.id, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error creating blog post:', err);
    return { id: null, error: err };
  }
};

/**
 * Fetches a single post by id for the preview page, regardless of its
 * status (draft/scheduled/published) — gated by matching previewToken
 * instead of auth, since this is meant to be shared with people who
 * aren't platform admins. CAVEAT: this only obscures the draft behind
 * an unguessable link; it does not stop someone who already has direct
 * read access to the blogPosts collection from finding it another way.
 * Returns null (not an error) on any mismatch or missing doc, so the
 * preview page renders a generic "not found" without distinguishing
 * "wrong token" from "doesn't exist."
 *
 * @param {string} postId
 * @param {string} token
 * @returns {Promise<{ post: (object|null), error: (Error|null) }>}
 */
export const getPostForPreview = async (postId, token) => {
  try {
    const snap = await getDoc(doc(db, BLOG_COLLECTION, postId));
    if (!snap.exists()) return { post: null, error: null };
    const data = snap.data();
    if (!token || !data.previewToken || data.previewToken !== token) {
      return { post: null, error: null };
    }
    return { post: { id: snap.id, ...data }, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error fetching post for preview:', err);
    return { post: null, error: err };
  }
};

/**
 * Fetch every post regardless of status (draft + published), for the
 * admin Post Manager table. No status filter means no composite index
 * is needed — a plain orderBy on publishedAt is a single-field index,
 * which Firestore provides automatically.
 *
 * @returns {Promise<{ posts: Array<object>, error: (Error|null) }>}
 */
export const getAllPostsForAdmin = async () => {
  try {
    await publishDueScheduledPosts();
    const q = query(collection(db, BLOG_COLLECTION), orderBy('publishedAt', 'desc'));
    const snap = await getDocs(q);
    const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { posts, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error fetching all posts for admin:', err);
    return { posts: [], error: err };
  }
};

/**
 * Update an existing post by its Firestore document id. Only succeeds if
 * the signed-in user is a platformAdmin, per the blogPosts security rule.
 *
 * @param {string} postId
 * @param {object} updates - partial fields to merge into the doc
 * @returns {Promise<{ success: boolean, error: (Error|null) }>}
 */
export const updateBlogPost = async (postId, updates) => {
  try {
    await updateDoc(doc(db, BLOG_COLLECTION, postId), updates);
    return { success: true, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error updating blog post:', err);
    return { success: false, error: err };
  }
};

const MAX_VERSION_HISTORY = 20;

/**
 * Builds a lightweight snapshot of a post's editable content, for
 * pushing onto its versionHistory array before an edit overwrites it.
 * Uses a client Date.now() timestamp — Firestore's serverTimestamp()
 * sentinel isn't allowed inside array elements, only top-level/map fields.
 *
 * @param {object} post - the post's current data (pre-edit)
 * @returns {object} version snapshot
 */
export const buildVersionSnapshot = (post) => ({
  title: post.title || '',
  excerpt: post.excerpt || '',
  contentBody: post.contentBody || '',
  quoteText: post.quoteText || null,
  type: post.type || 'article',
  status: post.status || 'draft',
  savedAt: Date.now(),
});

/**
 * Appends a version snapshot to an existing history array, trimming to
 * the most recent MAX_VERSION_HISTORY entries (oldest drop off first).
 *
 * @param {object[]} existingHistory
 * @param {object} snapshot
 * @returns {object[]}
 */
export const appendToVersionHistory = (existingHistory, snapshot) => {
  const next = [...(Array.isArray(existingHistory) ? existingHistory : []), snapshot];
  return next.slice(-MAX_VERSION_HISTORY);
};

/**
 * Silent autosave for the composer — patches just the editable content
 * fields plus a lastAutosavedAt stamp, without touching status/slug.
 * Intended to run on a debounce timer while editing an existing post.
 *
 * @param {string} postId
 * @param {object} fields - { title, excerpt, contentBody, quoteText }
 * @returns {Promise<{ error: (Error|null) }>}
 */
export const autosaveDraft = async (postId, fields) => {
  try {
    await updateDoc(doc(db, BLOG_COLLECTION, postId), {
      ...fields,
      lastAutosavedAt: serverTimestamp(),
    });
    return { error: null };
  } catch (err) {
    console.error('[blogHelpers] Error autosaving draft:', err);
    return { error: err };
  }
};

/**
 * Permanently delete a post by its Firestore document id.
 *
 * @param {string} postId
 * @returns {Promise<{ success: boolean, error: (Error|null) }>}
 */
export const deleteBlogPostById = async (postId) => {
  try {
    await deleteDoc(doc(db, BLOG_COLLECTION, postId));
    return { success: true, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error deleting blog post:', err);
    return { success: false, error: err };
  }
};

/**
 * Check whether a slug is already taken (published or not), so the
 * composer can warn before writing a duplicate.
 *
 * @param {string} slug
 * @returns {Promise<boolean>}
 */

/**
 * Permanently delete a post. Only succeeds if the signed-in user is a
 * platformAdmin, per the blogPosts security rule.
 *
 * @param {string} postId
 * @returns {Promise<{ error: (Error|null) }>}
 */
export const deleteBlogPost = async (postId) => {
  try {
    await deleteDoc(doc(db, BLOG_COLLECTION, postId));
    return { error: null };
  } catch (err) {
    console.error('[blogHelpers] Error deleting blog post:', err);
    return { error: err };
  }
};

/**
 * Apply the same partial update to many posts at once (bulk
 * publish/unpublish/feature/unfeature), via Firestore batched writes.
 * Chunked at 450 to stay under Firestore's 500-write batch limit —
 * blog volumes won't get close, but this won't silently break later.
 *
 * @param {string[]} postIds
 * @param {object} updates - partial fields to merge into every doc
 * @returns {Promise<{ error: (Error|null) }>}
 */
export const bulkUpdatePosts = async (postIds, updates) => {
  try {
    const CHUNK_SIZE = 450;
    for (let i = 0; i < postIds.length; i += CHUNK_SIZE) {
      const batch = writeBatch(db);
      postIds.slice(i, i + CHUNK_SIZE).forEach((id) => {
        batch.update(doc(db, BLOG_COLLECTION, id), updates);
      });
      await batch.commit();
    }
    return { error: null };
  } catch (err) {
    console.error('[blogHelpers] Error bulk-updating posts:', err);
    return { error: err };
  }
};

/**
 * Permanently delete many posts at once, via Firestore batched writes
 * (chunked at 450, same reasoning as bulkUpdatePosts).
 *
 * @param {string[]} postIds
 * @returns {Promise<{ error: (Error|null) }>}
 */
export const bulkDeletePosts = async (postIds) => {
  try {
    const CHUNK_SIZE = 450;
    for (let i = 0; i < postIds.length; i += CHUNK_SIZE) {
      const batch = writeBatch(db);
      postIds.slice(i, i + CHUNK_SIZE).forEach((id) => {
        batch.delete(doc(db, BLOG_COLLECTION, id));
      });
      await batch.commit();
    }
    return { error: null };
  } catch (err) {
    console.error('[blogHelpers] Error bulk-deleting posts:', err);
    return { error: err };
  }
};

/**
 * Sweeps for any post whose status is 'scheduled' and whose
 * scheduledFor time has passed, and flips it to 'published'. There's
 * no Cloud Functions cron in this project (avoiding the paid Blaze
 * plan), so this runs opportunistically inside every public read
 * function below instead of on a real schedule. Filters by
 * scheduledFor client-side rather than in the query, so this never
 * needs a Firestore composite index (same pattern as
 * getAllPostsForAdmin/subscribeCuratedPosts elsewhere in this file).
 *
 * @returns {Promise<{ publishedCount: number, error: (Error|null) }>}
 */
export const publishDueScheduledPosts = async () => {
  try {
    const q = query(collection(db, BLOG_COLLECTION), where('status', '==', 'scheduled'));
    const snap = await getDocs(q);
    const now = Date.now();
    const due = snap.docs.filter((d) => {
      const scheduledFor = d.data().scheduledFor;
      return typeof scheduledFor === 'number' && scheduledFor <= now;
    });
    if (due.length === 0) return { publishedCount: 0, error: null };
    const batch = writeBatch(db);
    due.forEach((d) => {
      batch.update(d.ref, { status: 'published', publishedAt: serverTimestamp() });
    });
    await batch.commit();
    return { publishedCount: due.length, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error publishing due scheduled posts:', err);
    return { publishedCount: 0, error: err };
  }
};

/**
 * Fetch published blog posts, most recent first.
 * Used for the standalone /blog index page.
 *
 * @param {number} [count] - optional max number of posts to return
 * @returns {Promise<{ posts: Array<object>, error: (Error|null) }>}
 */
export const getPublishedPosts = async (count) => {
  try {
    await publishDueScheduledPosts();
    const constraints = [
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
    ];
    if (count) constraints.push(fsLimit(count));

    const q = query(...constraints);
    const snap = await getDocs(q);
    const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { posts, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error fetching published posts:', err);
    return { posts: [], error: err };
  }
};

/**
 * Fetch a small, featured set of posts for the home landing page feed.
 *
 * @param {number} [count=3] - number of posts to show on the home page
 * @returns {Promise<{ posts: Array<object>, error: (Error|null) }>}
 */
export const getFeaturedPosts = async (count = 3) => {
  try {
    await publishDueScheduledPosts();
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'published'),
      where('featured', '==', true),
      orderBy('publishedAt', 'desc'),
      fsLimit(count)
    );
    const snap = await getDocs(q);
    let posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Fallback: if fewer than `count` posts are marked featured, top up
    // with the latest published posts so the home page section is never
    // sparse just because nobody flagged enough posts as featured yet.
    if (posts.length < count) {
      const { posts: latest } = await getPublishedPosts(count);
      const seenIds = new Set(posts.map((p) => p.id));
      for (const p of latest) {
        if (posts.length >= count) break;
        if (!seenIds.has(p.id)) posts.push(p);
      }
    }

    return { posts, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error fetching featured posts:', err);
    return { posts: [], error: err };
  }
};

/**
 * Fetch a single published post by its slug.
 * Used for the /blog/:slug article page.
 *
 * @param {string} slug
 * @returns {Promise<{ post: (object|null), error: (Error|null) }>}
 */
export const getPostBySlug = async (slug) => {
  try {
    await publishDueScheduledPosts();
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('slug', '==', slug),
      where('status', '==', 'published'),
      fsLimit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { post: null, error: null };
    const docSnap = snap.docs[0];
    return { post: { id: docSnap.id, ...docSnap.data() }, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error fetching post by slug:', err);
    return { post: null, error: err };
  }
};

/**
 * Fetch a single post by its Firestore document id.
 * Handy for admin/editing flows later.
 *
 * @param {string} postId
 * @returns {Promise<{ post: (object|null), error: (Error|null) }>}
 */
export const getPostById = async (postId) => {
  try {
    const snap = await getDoc(doc(db, BLOG_COLLECTION, postId));
    if (!snap.exists()) return { post: null, error: null };
    return { post: { id: snap.id, ...snap.data() }, error: null };
  } catch (err) {
    console.error('[blogHelpers] Error fetching post by id:', err);
    return { post: null, error: err };
  }
};